import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FormInputController,
  FormTextInput,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import { enumMapper } from '@wajeez-web/core/utils';
import { Autocomplete } from '@wajeez-web/components/mui';
import {
  SystemConfigsActions,
  useContentDispatch,
  useContentSelector,
} from '@wajeez-web/stores/content';

type Input = Server.UpdateSystemConfigInput;

interface SystemConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  id: number;
  onSubmit?: () => void | Promise<void>;
}

export const SystemConfigModal = ({
  isOpen,
  onClose,
  id,
  onSubmit,
}: SystemConfigModalProps) => {
  const contentDispatch = useContentDispatch();

  const editSystemConfigStatus = useContentSelector(
    ({ systemConfigs }) => systemConfigs.editSystemConfig.status
  );
  const systemConfigById = useContentSelector(
    ({ systemConfigs }) => systemConfigs.systemConfigById
  );

  const { handleSubmit, control, reset } = useForm<Input>();

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    const res = await contentDispatch(
      SystemConfigsActions.editSystemConfigAsync(data)
    );

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && (await onSubmit());
    }
  };

  useEffect(() => {
    contentDispatch(SystemConfigsActions.getSystemConfigByIdAsync(id));
  }, [contentDispatch, id]);

  useEffect(() => {
    if (systemConfigById.data) {
      reset(systemConfigById.data);
    }
  }, [systemConfigById.data]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="System Config"
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
    >
      <div className="w-full">
        {systemConfigById.status === 'loading' ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="w-full flex flex-col justify-start items-start"
          >
            <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
              <FormTextInput
                name="value"
                control={control}
                fieldType={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: {},
                  label: 'Value',
                  placeHolder: 'Value',
                  required: true,
                }}
                heightClassName="h-[34px]"
                widthClassName="w-full"
              />
            </div>
            <div className="mt-6 w-full flex justify-end items-center space-s-2">
              <div className="w-[70px]">
                <ButtonHeadless
                  bgStyle="bg-warmGray-300"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={onClose}
                >
                  <div className="text-warmGray-600 text-[16px] font-medium">
                    <ReturnTranslatedText
                      text="buttons.Cancel"
                      origintext="Cancel"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[70px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Save"
                      origintext="Save"
                    />
                    {editSystemConfigStatus === 'loading' && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
